import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = document.body.style.position === 'fixed'
      ? (isNaN(parseFloat(document.body.style.top)) ? 0 : -parseFloat(document.body.style.top))
      : window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      setTimeout(() => {
        dialog.addEventListener('click', this.#handleClick);
        this.addEventListener('click', this.#handleClick);
        window.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      }, 0);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog || !dialog.open || dialog.classList.contains('dialog-closing')) return;

    dialog?.removeEventListener('click', this.#handleClick);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, 300)),
    ]);

    const activeDialogs = Array.from(document.querySelectorAll('dialog')).filter(
      (d) => d.open && d !== dialog
    );

    if (activeDialogs.length === 0) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of the inner container.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  closeOnOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open || dialog.classList.contains('dialog-closing')) return;

    const target = /** @type {Element | null} */ (event.target);
    if (!target) return;

    const innerElement =
      dialog.querySelector('.shop-the-look-drawer__inner') ||
      dialog.querySelector('.dialog-drawer__inner') ||
      dialog.querySelector('.dialog-modal__inner') ||
      dialog.querySelector('.facets-drawer__form-wrapper') ||
      dialog.querySelector('.facets__form-wrapper');

    // Check if the click target or event path originated inside innerElement or inside dialog (excluding dialog itself)
    const isInsideDrawerContent =
      (innerElement && (innerElement === target || innerElement.contains(target))) ||
      (dialog.contains(target) && target !== dialog) ||
      (event.composedPath &&
        event.composedPath().some(
          (node) =>
            node === innerElement ||
            (node instanceof HTMLElement &&
              node.matches &&
              node.matches('.shop-the-look-drawer__inner, .dialog-drawer__inner, .dialog-modal__inner, .facets-drawer__form-wrapper, .facets__form-wrapper'))
        ));

    if (isInsideDrawerContent) {
      return; // Inside click (size select, add button, product row, etc.) -> DO NOT CLOSE
    }

    if (target.closest?.('.shop-the-look__trigger, [on\\:click*="showDialog"]')) return;

    if (innerElement) {
      const { left, right, top, bottom } = innerElement.getBoundingClientRect();
      const { clientX, clientY } = event;

      if (clientX !== undefined && clientY !== undefined && (clientX !== 0 || clientY !== 0)) {
        const isInsideRect = clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
        if (!isInsideRect) {
          this.closeDialog();
          return;
        }
      } else if (target === dialog) {
        this.closeDialog();
        return;
      }
    } else if (target === dialog) {
      this.closeDialog();
      return;
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    this.closeOnOutsideClick(event);
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
